import { useState, useCallback, useContext, useEffect } from 'react'
import { initializeStepStatusArray } from '../utils/stepHelpers'
import { getBreakStatus } from 'services/break'
import { userTypeEnum } from 'services/user'
import { userContext } from 'global/context/sub-contexts/userContext'

export const useStepStatus = (brk, userType) => {
  const { unauthorized } = useContext(userContext)
  const hasStartScreen = brk.hasLandingScreen !== false

  const [stepStatusArr, setStepStatusArr] = useState(() =>
    initializeStepStatusArray(brk, hasStartScreen),
  )

  // Sync step completion state with the API on mount for registered users
  useEffect(() => {
    if (userType !== userTypeEnum.REGISTERED) return

    getBreakStatus(brk.id)
      .then(result => {
        if (result.success !== true) return

        setStepStatusArr(prev => {
          const next = JSON.parse(JSON.stringify(prev))

          result.detail.forEach(apiItem => {
            next.forEach((statusItem, index) => {
              if (apiItem.stepId === statusItem.id && apiItem.completeTime) {
                next[index].complete = true
                next[index].locked = false
              }
            })
          })

          // Match legacy class behavior: landing entry (never complete)
          // short-circuits this loop at index 0, so only steps already
          // marked complete by the receipt API get unlocked here.
          let previousComplete = true
          next.forEach((statusItem, index) => {
            if (previousComplete) next[index].locked = false
            if (!statusItem.complete) previousComplete = false
          })

          return JSON.stringify(prev) !== JSON.stringify(next) ? next : prev
        })
      })
      .catch(e => {
        if (e.message === '401') {
          console.log("can't get break status - user not signed in")
          unauthorized()
        } else {
          console.warn('BREAK STATUS ERROR', e)
        }
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateStepStatus = useCallback(updater => {
    setStepStatusArr(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      updater(next)
      return JSON.stringify(prev) !== JSON.stringify(next) ? next : prev
    })
  }, [])

  const getIsLocked = useCallback(
    stepId => {
      if (stepId === 'landing') return false
      const found = stepStatusArr.find(s => s.id === stepId)
      if (!found) {
        console.warn(`Unable to find step status for ${stepId}`)
        return true
      }
      return found.locked
    },
    [stepStatusArr],
  )

  const getIsComplete = useCallback(
    stepId => {
      const found = stepStatusArr.find(s => s.id === stepId)
      if (!found) {
        console.warn(`Unable to find step status for ${stepId}`)
        return false
      }
      return found.complete
    },
    [stepStatusArr],
  )

  const getIsScrollComplete = useCallback(
    stepId => {
      const found = stepStatusArr.find(s => s.id === stepId)
      if (!found) {
        console.warn(`Unable to find step status for ${stepId}`)
        return false
      }
      return found.scrollComplete
    },
    [stepStatusArr],
  )

  const unlockNextStep = useCallback(
    stepId => {
      updateStepStatus(arr => {
        arr.forEach((statusItem, i) => {
          if (statusItem.id === stepId && i < arr.length - 1) {
            arr[i + 1].locked = false
          }
        })
      })
    },
    [updateStepStatus],
  )

  const scrollCompleteStep = useCallback(
    stepId => {
      updateStepStatus(arr => {
        const item = arr.find(s => s.id === stepId)
        if (item) item.scrollComplete = true
      })
    },
    [updateStepStatus],
  )

  const markStepComplete = useCallback(
    stepId => {
      updateStepStatus(arr => {
        arr.forEach((statusItem, i) => {
          if (statusItem.id === stepId) {
            arr[i].complete = true
            if (i < arr.length - 1) arr[i + 1].locked = false
          }
        })
      })
    },
    [updateStepStatus],
  )

  const markStepSkipped = useCallback(
    stepId => {
      updateStepStatus(arr => {
        const item = arr.find(s => s.id === stepId)
        if (item) {
          item.skipped = true
          item.complete = true
        }
      })
    },
    [updateStepStatus],
  )

  return {
    stepStatusArr,
    getIsLocked,
    getIsComplete,
    getIsScrollComplete,
    unlockNextStep,
    scrollCompleteStep,
    markStepComplete,
    markStepSkipped,
  }
}
